import { useTranslation } from 'react-i18next'

import { EMPTY_LIST } from '../../../../../constants/common'
import {
  RENTAL_PORTAL_FACT_SEPARATOR,
  RENTAL_PORTAL_VIEWING_STATUSES,
} from '../../../../../constants/rentalPortal'
import {
  useDateLocale,
  useLinkLikeClick,
  useTimeZone,
} from '../../../../../hooks'
import Button, { BUTTON_HEIGHT_32 } from '../../../../Button'
import { EntityStatus } from '../../../../Entity'
import { getRentalPortalApplicantIdentityLabel } from '../../../../RentalPortalApplicant'
import { getListingUnitLabel } from '../../../../RentalPortalListing'
import {
  getViewingObjectName,
  getViewingProfileLink,
  getViewingTimeRangeLabel,
  IS_VIEWING_BOOKING_AVAILABLE,
  useBookRentalPortalViewing,
} from '../../../../RentalPortalViewing'
import Text from '../../../../Text'
import { Widget } from '../../../../Widget'
import { useApplicantProfileContext } from '../../RentalPortalApplicantProfile.hooks'

import {
  QA_NAME_APPLICANT_VIEWING_CARD,
  QA_NAME_APPLICANT_VIEWING_CARD_BOOK,
  QA_NAME_APPLICANT_VIEWING_CARD_OPEN,
  QA_NAME_APPLICANT_VIEWING_CARD_RETRY,
} from './ApplicantViewingCard.constants'
import { useApplicantViewingCard } from './ApplicantViewingCard.hooks'
import { getApplicantCurrentViewing } from './ApplicantViewingCard.utils'

import * as styles from './ApplicantViewingCard.module.css'

import RefreshIcon from '../../../../../icons/refresh.svg'

function ApplicantViewingCard() {
  const { t } = useTranslation()

  const { handleClick: handleLinkClick } = useLinkLikeClick()

  const locale = useDateLocale()
  const timezone = useTimeZone()

  const { applicantData, isPreview } = useApplicantProfileContext()

  const { handleRefresh, hasError, isInitiated, items } =
    useApplicantViewingCard({ applicantId: applicantData?.id })

  const { bookViewingModal, handleOpenBookViewing } =
    useBookRentalPortalViewing({
      onBooked: handleRefresh,
    })

  const viewing = getApplicantCurrentViewing(items ?? EMPTY_LIST)

  function handleClickOpenViewing(event) {
    handleLinkClick({ event, link: getViewingProfileLink(viewing.id) })
  }

  function handleClickBookViewing() {
    const listingLabel = getListingUnitLabel({ listing: applicantData, t })

    handleOpenBookViewing({
      applicant: {
        label: getRentalPortalApplicantIdentityLabel({
          applicant: applicantData,
          t,
        }),
        stage: applicantData.stage,
        value: applicantData.id,
      },
      listing: listingLabel
        ? { label: listingLabel, value: applicantData.listing_id }
        : null,
    })
  }

  return (
    <Widget
      headerProps={{ contentProps: { actionsProps: { EditButton: null } } }}
      isSkeleton={!isInitiated}
      qaName={QA_NAME_APPLICANT_VIEWING_CARD}
      title={t('RentalPortal.ApplicantProfileTabViewingsKey')}
    >
      <div className={styles.content}>
        {hasError ? (
          <>
            <Text isSecondary>
              {t('RentalPortal.ApplicantViewingCardLoadErrorKey')}
            </Text>
            <Button.Flat
              beforeIconProps={{ Icon: RefreshIcon }}
              qaName={QA_NAME_APPLICANT_VIEWING_CARD_RETRY}
              text={t('RentalPortal.ListingRetryBtnKey')}
              onClick={handleRefresh}
            />
          </>
        ) : viewing ? (
          <>
            <div className={styles.summary}>
              <EntityStatus
                id={viewing.status}
                statuses={RENTAL_PORTAL_VIEWING_STATUSES}
              />
              <Text className={styles.label}>
                {`${getViewingObjectName({ locale, t, timezone, viewing })}${RENTAL_PORTAL_FACT_SEPARATOR}${getViewingTimeRangeLabel(
                  {
                    end: viewing.end,
                    locale,
                    start: viewing.start,
                    timezone,
                  }
                )}`}
              </Text>
            </div>
            {isPreview ? null : (
              <div className={styles.actions}>
                <Button.Outlined
                  isGrey
                  height={BUTTON_HEIGHT_32}
                  qaName={QA_NAME_APPLICANT_VIEWING_CARD_OPEN}
                  text={t('RentalPortal.ViewingProfileOpenBtnKey')}
                  onClick={handleClickOpenViewing}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <Text isSecondary>
              {t('RentalPortal.ApplicantViewingCardEmptyKey')}
            </Text>
            {isPreview ? null : (
              <div className={styles.actions}>
                <Button.Outlined
                  isGrey
                  disabled={!IS_VIEWING_BOOKING_AVAILABLE}
                  height={BUTTON_HEIGHT_32}
                  qaName={QA_NAME_APPLICANT_VIEWING_CARD_BOOK}
                  text={t('RentalPortal.ListingProfileFooterBookViewingBtnKey')}
                  tooltip={
                    IS_VIEWING_BOOKING_AVAILABLE
                      ? null
                      : t(
                          'RentalPortal.ListingProfileFooterBookViewingUnavailableTooltipKey'
                        )
                  }
                  onClick={handleClickBookViewing}
                />
              </div>
            )}
          </>
        )}
      </div>
      {bookViewingModal}
    </Widget>
  )
}

export default ApplicantViewingCard
